import { useState } from 'react'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import useResource from '../../../../../io/useResource'
import { VersionRecord } from '../../../../../io/api/entity/data/listVersions'
import fetchVersionSnapshot from '../../../../../io/api/entity/data/fetchVersionSnapshot'
import MergeEditor from '../../../../../components/data/MergeEditor'
import DiffStats from './DiffStats'
import EntityDiffSummary from './EntityDiffSummary'
import { diffSnapshots } from './entitySnapshotDiff'
import EntityType = com.eidu.sharedlib.entity.type.EntityType

type VersionDiffDialogProps = {
  onClose: () => void
  records: VersionRecord[]
  initialLeftVersionId: string
  initialRightVersionId: string
  entityType?: EntityType
}

const useSnapshot = (snapshotResourceKey: string | undefined) =>
  useQuery({
    queryKey: ['versionSnapshot', snapshotResourceKey],
    queryFn: () => fetchVersionSnapshot(snapshotResourceKey!),
    enabled: !!snapshotResourceKey,
  })

const shortVersionId = (versionId: string): string => versionId.split('-')[0] ?? versionId.slice(0, 8)
const absoluteTimestamp = (ms: number): string => new Date(ms).toLocaleString()

const VersionSelect = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (versionId: string) => void
  options: VersionRecord[]
}) => (
  <FormControl size="small" sx={{ minWidth: 220 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      MenuProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 3 } }}
    >
      {options.map((r) => (
        <MenuItem key={r.versionId} value={r.versionId}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {shortVersionId(r.versionId)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {absoluteTimestamp(r.createdAt)}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

const VersionDiffDialog = ({
  onClose,
  records,
  initialLeftVersionId,
  initialRightVersionId,
  entityType,
}: VersionDiffDialogProps) => {
  const [leftVersionId, setLeftVersionId] = useState(initialLeftVersionId)
  const [rightVersionId, setRightVersionId] = useState(initialRightVersionId)

  const left = records.find((r) => r.versionId === leftVersionId)
  const right = records.find((r) => r.versionId === rightVersionId)

  const { content: leftContent, isLoading: leftLoading, error: leftError } = useResource(left?.payloadKey ?? undefined)
  const {
    content: rightContent,
    isLoading: rightLoading,
    error: rightError,
  } = useResource(right?.payloadKey ?? undefined)

  const leftSnapshotQuery = useSnapshot(left?.snapshotResourceKey)
  const rightSnapshotQuery = useSnapshot(right?.snapshotResourceKey)
  const entityDiff =
    leftSnapshotQuery.data && rightSnapshotQuery.data
      ? diffSnapshots(leftSnapshotQuery.data, rightSnapshotQuery.data)
      : undefined

  const loading = leftLoading || rightLoading
  const error = leftError || rightError
  const ready = !loading && !error && leftContent !== undefined && rightContent !== undefined

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: { height: '85vh' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Stack>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
            Compare
          </Typography>
          <Typography variant="h6">Version diff</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1.5, alignItems: 'center' }}>
            <VersionSelect label="Base" value={leftVersionId} onChange={setLeftVersionId} options={records} />
            <Typography color="text.secondary">→</Typography>
            <VersionSelect label="Compare" value={rightVersionId} onChange={setRightVersionId} options={records} />
            {ready && <DiffStats left={leftContent} right={rightContent} />}
          </Stack>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="Close diff"
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {entityDiff && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Fields & links
            </Typography>
            <EntityDiffSummary
              diff={entityDiff}
              entityType={entityType}
              beforeLabel={`Base · ${shortVersionId(leftVersionId)}`}
              afterLabel={`Compare · ${shortVersionId(rightVersionId)}`}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            Payload
          </Typography>
          {loading && (
            <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Stack>
          )}
          {error && (
            <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', px: 3 }}>
              <Typography color="error">Failed to load diff: {error.message}</Typography>
            </Stack>
          )}
          {ready && <MergeEditor left={leftContent} right={rightContent} />}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default VersionDiffDialog
